import type { QueryClient } from "@tanstack/react-query"
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  notFound,
  Outlet,
  type RouterHistory,
  redirect,
} from "@tanstack/react-router"
import { AppPending } from "./components/app-pending"
import { NotFound } from "./components/not-found"
import { RouteError } from "./components/route-error"
import { verifiedSearch } from "./components/verify-banner"
import { WorkspaceNoAccess } from "./components/workspace-no-access"
import { validateAgentSearch } from "./features/agent-page/agent-search"
import { validateDocumentSearch } from "./features/documents/documents-model"
import { validateKnowledgeSearch } from "./features/knowledge/knowledge-model"
import {
  validateKnowledgeIndexSearch,
  validateVersionSearch,
} from "./features/workspace-knowledge/knowledge-model"
import { authClient } from "./lib/auth-client"
import { pickWorkspace, readLastWorkspace, writeLastWorkspace } from "./lib/last-workspace"
import { continueHref, isOAuthPage, OAUTH_CONSENT_PATH, OAUTH_CONTINUE_PATH, rawSearch } from "./lib/oauth"
import { loadProjectPages } from "./lib/project-chunk"
import { legacyProjectSearch, validateProjectSearch } from "./lib/project-search"
import { sessionQuery, workspacesQuery } from "./lib/queries"
import { safeRedirect } from "./lib/redirect"
import { warmProjectPage } from "./model/live/warm"
import { ForgotPasswordPage } from "./routes/forgot-password"
import { InvitePage } from "./routes/invite"
import { OAuthConsentPage } from "./routes/oauth-consent"
import { OAuthContinuePage } from "./routes/oauth-continue"
import { dropResetToken, holdResetToken, RESET_TOKEN, ResetPasswordPage } from "./routes/reset-password"
import { SignInPage } from "./routes/sign-in"
import { SignUpPage } from "./routes/sign-up"
import { WelcomePage } from "./routes/welcome"
import { WorkspaceHome } from "./routes/workspace"

/**
 * Decision 0151: pages off the boot path (Home, a project) load their code when first opened, or on hover with the
 * router's intent preload: settings, account, runners, knowledge, an agent's, a document's and a memory's page, pairing.
 * The entry chunk no longer carries them.
 */
const accountPages = () => import("./routes/account")
const projectSettingsPages = () => import("./routes/project-settings")
const settingsPages = () => import("./routes/settings")
const knowledgePages = () => import("./features/workspace-knowledge/knowledge-routes")
const AccountAppsPage = lazyRouteComponent(accountPages, "AccountAppsPage")
const AccountIndex = lazyRouteComponent(accountPages, "AccountIndexPage")
const AccountLayout = lazyRouteComponent(accountPages, "AccountLayout")
const AccountMemoryPage = lazyRouteComponent(accountPages, "AccountMemoryPage")
const AccountNotificationsPage = lazyRouteComponent(accountPages, "AccountNotificationsPage")
const AccountSecurityPage = lazyRouteComponent(accountPages, "AccountSecurityPage")
const AccountUsagePage = lazyRouteComponent(accountPages, "AccountUsagePage")
const AccountSubscriptionsPage = lazyRouteComponent(accountPages, "AccountSubscriptionsPage")
const AgentRoutePage = lazyRouteComponent(() => import("./features/agent-page/agent-route"), "AgentRoutePage")
const CapabilitiesRoutePage = lazyRouteComponent(knowledgePages, "CapabilitiesRoutePage")
const DocumentRoutePage = lazyRouteComponent(
  () => import("./features/documents/document-route"),
  "DocumentRoutePage",
)
const GuidelineRoutePage = lazyRouteComponent(knowledgePages, "GuidelineRoutePage")
const KnowledgeRoutePage = lazyRouteComponent(knowledgePages, "KnowledgeRoutePage")
const MemoryRoutePage = lazyRouteComponent(() => import("./features/memory/memory-route"), "MemoryRoutePage")
const GoalRoutePage = lazyRouteComponent(loadProjectPages, "GoalRoutePage")
const ItemRoutePage = lazyRouteComponent(loadProjectPages, "ItemRoutePage")
const KpiRoutePage = lazyRouteComponent(loadProjectPages, "KpiRoutePage")
const PairPage = lazyRouteComponent(() => import("./routes/pair"), "PairPage")
const PersonRoutePage = lazyRouteComponent(knowledgePages, "PersonRoutePage")
const ProjectPage = lazyRouteComponent(loadProjectPages, "ProjectPage")
const ProjectRoute = lazyRouteComponent(loadProjectPages, "ProjectRoute")
const ProjectAgentNewPage = lazyRouteComponent(projectSettingsPages, "ProjectAgentNewPage")
const ProjectAgentPage = lazyRouteComponent(projectSettingsPages, "ProjectAgentPage")
const ProjectAgentsPage = lazyRouteComponent(projectSettingsPages, "ProjectAgentsPage")
const ProjectAllowlistPage = lazyRouteComponent(projectSettingsPages, "ProjectAllowlistPage")
const ProjectAreasPage = lazyRouteComponent(projectSettingsPages, "ProjectAreasPage")
const ProjectGeneralPage = lazyRouteComponent(projectSettingsPages, "ProjectGeneralPage")
const ProjectMembersPage = lazyRouteComponent(projectSettingsPages, "ProjectMembersPage")
const ProjectReposPage = lazyRouteComponent(projectSettingsPages, "ProjectReposPage")
const ProjectSecretsPage = lazyRouteComponent(projectSettingsPages, "ProjectSecretsPage")
const ProjectSettingsIndex = lazyRouteComponent(projectSettingsPages, "ProjectSettingsIndex")
const ProjectSettingsLayout = lazyRouteComponent(projectSettingsPages, "ProjectSettingsLayout")
const ProjectWorkersPage = lazyRouteComponent(projectSettingsPages, "ProjectWorkersPage")
const RunnerDetailPage = lazyRouteComponent(() => import("./routes/runner-detail"), "RunnerDetailPage")
const RunnerNewPage = lazyRouteComponent(() => import("./routes/runner-new"), "RunnerNewPage")
const RunnersPage = lazyRouteComponent(() => import("./routes/runners"), "RunnersPage")
const SearchRoutePage = lazyRouteComponent(loadProjectPages, "SearchRoutePage")
const SettingsAllowlistPage = lazyRouteComponent(settingsPages, "SettingsAllowlistPage")
const SettingsGeneralPage = lazyRouteComponent(settingsPages, "SettingsGeneralPage")
const SettingsGithubPage = lazyRouteComponent(settingsPages, "SettingsGithubPage")
const SettingsIndex = lazyRouteComponent(settingsPages, "SettingsIndex")
const SettingsLayout = lazyRouteComponent(settingsPages, "SettingsLayout")
const SettingsMembersPage = lazyRouteComponent(settingsPages, "SettingsMembersPage")
const SettingsModelsPage = lazyRouteComponent(settingsPages, "SettingsModelsPage")
const SettingsSecretsPage = lazyRouteComponent(settingsPages, "SettingsSecretsPage")
const SettingsSubscriptionsPage = lazyRouteComponent(settingsPages, "SettingsSubscriptionsPage")
const SettingsUsagePage = lazyRouteComponent(settingsPages, "SettingsUsagePage")

export interface RouterContext {
  queryClient: QueryClient
}

/**
 * The routes that wait on the server before anything can render (the session, the workspace list) show `AppPending`
 * after 300 ms instead of a blank page, and a failed or timed-out wait shows `RouteError` (decision 0034). Not router
 * defaults: in-app navigations keep showing the current page while the next one loads. The error component is the
 * root's too (the root renders only an Outlet, so it looks the same); naming it here also covers the server render.
 */
export const WAITING_PENDING = {
  pendingComponent: AppPending,
  pendingMs: 300,
  errorComponent: RouteError,
} as const

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  ...WAITING_PENDING,
  component: Outlet,
  notFoundComponent: () => <NotFound />,
  errorComponent: RouteError,
})

type RedirectSearch = { redirect?: string }
const validateRedirect = (search: Record<string, unknown>): RedirectSearch => {
  const target = safeRedirect(search.redirect)
  return target ? { redirect: target } : {}
}

// Pages for signed-out users; bounce to the app (or the page that sent them here) if a session exists.
export const guestRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "guest",
  ...WAITING_PENDING,
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery)
    if (session) {
      const target = safeRedirect((location.search as RedirectSearch).redirect)
      // The OAuth pages read their signed query from a fresh page load (lib/oauth `rawSearch`).
      if (target && isOAuthPage(target)) throw redirect({ href: target, reloadDocument: true })
      throw target ? redirect({ href: target }) : redirect({ to: "/" })
    }
  },
  component: Outlet,
})

// Everything behind sign-in. Remembers where you were going (e.g. /pair?code=…).
export const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  ...WAITING_PENDING,
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery)
    if (!session) {
      const back = location.href === "/" ? undefined : location.href
      throw redirect({ to: "/sign-in", search: back ? { redirect: back } : {} })
    }
    return { session }
  },
  component: Outlet,
})

const signInRoute = createRoute({
  getParentRoute: () => guestRoute,
  path: "/sign-in",
  validateSearch: validateRedirect,
  component: SignInPage,
})
const signUpRoute = createRoute({
  getParentRoute: () => guestRoute,
  path: "/sign-up",
  validateSearch: validateRedirect,
  component: SignUpPage,
})

/** Forgot password (decision 0064): signed out only, like sign-in, and keeps where sign-in was going. */
const forgotPasswordRoute = createRoute({
  getParentRoute: () => guestRoute,
  path: "/forgot-password",
  validateSearch: validateRedirect,
  component: ForgotPasswordPage,
})

/**
 * The reset link of the email (decision 0064): works signed in and out, like an invite link. The token is taken out
 * of the address before the page renders: held in memory, then a `replace` redirect to the bare path, so it isn't
 * left in the address bar, the history or a copied URL (wave M spec §6.5).
 */
export const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  validateSearch: (search: Record<string, unknown>): { token?: string } =>
    typeof search.token === "string" && RESET_TOKEN.test(search.token) ? { token: search.token } : {},
  // `search` here also carries the raw query (the root validates nothing), so a malformed token is removed too.
  beforeLoad: ({ search, preload }) => {
    const raw: unknown = (search as { token?: unknown }).token
    if (raw === undefined || preload) return
    if (typeof raw === "string" && RESET_TOKEN.test(raw)) holdResetToken(raw)
    else dropResetToken()
    throw redirect({ to: "/reset-password", search: {}, replace: true })
  },
  component: ResetPasswordPage,
})

/** An invite link works signed in and out (earlier spec §5.2): no guard. */
export const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite/$invitationId",
  // decision 0130: the emailed link's key (`?k=`), so the preview names the invited address in full
  validateSearch: (search: Record<string, unknown>): { k?: string } =>
    typeof search.k === "string" && /^[A-Za-z0-9_-]{22}$/.test(search.k) ? { k: search.k } : {},
  component: InvitePage,
})

/** `/` resolves to a workspace (earlier spec §1.1): session's active one, this device's last one, the newest. */
export const homeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  ...WAITING_PENDING,
  // decision 0166 §5: the email's verify link lands on `/?verified=1`; /welcome says "Verified"
  validateSearch: verifiedSearch,
  beforeLoad: async ({ context, search }) => {
    const list = await context.queryClient.ensureQueryData(workspacesQuery)
    const pick = pickWorkspace(list, readLastWorkspace())
    if (!pick) throw redirect({ to: "/welcome", search, replace: true })
    throw redirect({ to: "/w/$workspaceId", params: { workspaceId: pick }, replace: true })
  },
  component: () => null,
})

/** Only for people with no workspace yet; everyone else goes through the resolver. */
export const welcomeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/welcome",
  validateSearch: verifiedSearch,
  beforeLoad: async ({ context }) => {
    const list = await context.queryClient.ensureQueryData(workspacesQuery)
    if (list.workspaces.length > 0) throw redirect({ to: "/", replace: true })
  },
  component: WelcomePage,
})

/**
 * The external MCP server's OAuth pages (external MCP spec §6.1, §6.2), outside the guest and app guards: signed out,
 * both go to sign-in and come back through `/oauth/continue`, which re-runs the client's authorize request. Their
 * search params are the provider's signed query and pass through untouched (no `validateSearch`).
 */
export const oauthContinueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: OAUTH_CONTINUE_PATH,
  ...WAITING_PENDING,
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery)
    if (!session)
      throw redirect({
        to: "/sign-in",
        search: { redirect: continueHref(rawSearch(OAUTH_CONTINUE_PATH, location.searchStr)) },
      })
  },
  component: OAuthContinuePage,
})

export const oauthConsentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: OAUTH_CONSENT_PATH,
  ...WAITING_PENDING,
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery)
    if (!session)
      throw redirect({
        to: "/sign-in",
        search: { redirect: continueHref(rawSearch(OAUTH_CONSENT_PATH, location.searchStr)) },
      })
  },
  component: OAuthConsentPage,
})

export const pairRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/pair",
  validateSearch: (search: Record<string, unknown>): { code?: string } =>
    typeof search.code === "string" && search.code ? { code: search.code } : {},
  component: PairPage,
})

/**
 * A workspace you are a member of. The URL is the truth (D1); entering one records it as "last used" on this device
 * and, fire and forget, as the session's active workspace (D2). Not a member → `WorkspaceNoAccess`.
 */
export const workspaceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/w/$workspaceId",
  // A direct load of a workspace URL waits on the workspace list here, after the session.
  ...WAITING_PENDING,
  beforeLoad: async ({ context, params, preload }) => {
    let list = await context.queryClient.ensureQueryData(workspacesQuery)
    // A workspace just created or joined may be missing from a cached list: ask the server once before "no access".
    if (!list.workspaces.some((w) => w.id === params.workspaceId))
      list = await context.queryClient.fetchQuery({ ...workspacesQuery, staleTime: 0 })
    const workspace = list.workspaces.find((w) => w.id === params.workspaceId)
    if (!workspace) throw notFound()
    // Hover preloads (defaultPreload "intent") run beforeLoad too: only a real entry may record "last used" (D2).
    if (preload) return { workspace }
    writeLastWorkspace(workspace.id)
    if (context.session.session.activeOrganizationId !== workspace.id) {
      void authClient.organization
        .setActive({ organizationId: workspace.id })
        .then((result) => {
          if (result.error) return
          context.queryClient.setQueryData(sessionQuery.queryKey, (prev) =>
            prev ? { ...prev, session: { ...prev.session, activeOrganizationId: workspace.id } } : prev,
          )
        })
        .catch(() => {})
    }
    return { workspace }
  },
  notFoundComponent: WorkspaceNoAccess,
  component: Outlet,
})

export const workspaceHomeRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/",
  component: WorkspaceHome,
})

/**
 * Decision 0086 (build map §6.1): Knowledge, the workspace's own place for people pages and guidelines (not a
 * project). `?tab=guidelines`, `?q=` searches; a page takes `?v=<n>` for an older version. The pages are a lazy chunk.
 */
export const knowledgeRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/knowledge",
  validateSearch: validateKnowledgeIndexSearch,
  component: KnowledgeRoutePage,
})
export const knowledgePersonRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/knowledge/people/$userId",
  validateSearch: validateVersionSearch,
  component: PersonRoutePage,
})
export const knowledgeGuidelineRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/knowledge/guidelines/$topic",
  validateSearch: validateVersionSearch,
  component: GuidelineRoutePage,
})
/** Decision 0100: `runners/capabilities.md`, what works where (leads keep it; linked from Settings › Runners). */
export const knowledgeCapabilitiesRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/knowledge/runners/capabilities",
  validateSearch: validateVersionSearch,
  component: CapabilitiesRoutePage,
})

/**
 * A project: the layout route holds the one live connection (W1) for the project page and its settings (X7), and the
 * search params every child shares (`view`, `node`, `run`, `runs`).
 */
export const projectRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/p/$projectId",
  validateSearch: validateProjectSearch,
  // Project load speed (2026-10-17): open the project's socket (and on Chat, its chunk and transcript) before the page
  // renders, and already when a project link is hovered (intent preload). Never waits: the page loads each piece too.
  beforeLoad: ({ context, params, search, preload }) => {
    warmProjectPage({
      queryClient: context.queryClient,
      workspaceId: params.workspaceId,
      projectId: params.projectId,
      userId: context.session.user.id,
      view: search.view,
      agent: search.agent,
      ...(preload ? { holdMs: PRELOAD_HOLD_MS } : {}),
    })
  },
  component: ProjectRoute,
})

/** A hovered project link holds its warmed socket this long (then the usual close grace). */
const PRELOAD_HOLD_MS = 10_000

/**
 * The five tabs (decision 0056). Old view URLs (`?view=areas|graph|list|roadmap|mine|triage|inbox`) redirect to their
 * tab, keeping `run`, `runs`, `focus` and `node`; the cloud still builds `?view=inbox` links for MCP.
 */
export const projectIndexRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/",
  beforeLoad: ({ location, params }) => {
    const search = legacyProjectSearch(new URLSearchParams(location.searchStr))
    if (search) throw redirect({ to: "/w/$workspaceId/p/$projectId", params, search, replace: true })
  },
  component: ProjectPage,
})

/** An item's page (decision 0035): `$ref` is its ref (`DIC-41`, or an old `T-12`, `I-3`, `C-57`) or its id. */
export const itemRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/i/$ref",
  component: ItemRoutePage,
})

/**
 * Wave K (decision 0065, spec §10.4): the search page. `q`, `in` (`history` for past discussions and runs), `kinds`,
 * and the history window (`range`, `since`, `until`).
 */
export const projectSearchRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/search",
  validateSearch: validateKnowledgeSearch,
  component: SearchRoutePage,
})

/** A goal's page: `$ref` is its ref (`G-1`) or its id. */
export const goalRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/g/$ref",
  component: GoalRoutePage,
})

/** Decision 0097: a KPI's page (its history over time and the work around it): `$ref` is its ref (`K-3`) or its id. */
export const kpiRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/k/$ref",
  component: KpiRoutePage,
})

/**
 * Wave DOC (decision 0068, spec §5.1): a document's page (any `O-n`; an output shows its preview). `?v=<n>` shows
 * version n of its chain.
 */
export const documentRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/o/$ref",
  validateSearch: validateDocumentSearch,
  component: DocumentRoutePage,
})

/**
 * Wave C (decision 0071, spec §3.1): an agent's page. `$handle` is her handle (a renamed handle's old link redirects
 * through `AgentByOldHandle`; her id works too). `?tab=knows|history|about|settings` (Now is the default).
 */
export const agentPageRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/agents/$handle",
  validateSearch: validateAgentSearch,
  component: AgentRoutePage,
})

/** Wave C (decision 0071, spec §3.4): a memory's page, `/m/M-7`. */
export const memoryRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/m/$ref",
  component: MemoryRoutePage,
})

/** 768 px and wider (the settings nav column shows); false without a window (server renders). */
const wideScreen = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(min-width: 768px)").matches

export const projectSettingsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/settings",
  component: ProjectSettingsLayout,
})
/**
 * Settings index: the phone shows the section list; 768 px and wider go straight to General. The redirect happens here,
 * before render: a `<Navigate>` inside the live project shell re-fired on every re-render and froze the tab.
 */
export const projectSettingsIndexRoute = createRoute({
  getParentRoute: () => projectSettingsRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    if (wideScreen())
      throw redirect({ to: "/w/$workspaceId/p/$projectId/settings/general", params, replace: true })
  },
  component: ProjectSettingsIndex,
})
export const projectGeneralRoute = createRoute({
  getParentRoute: () => projectSettingsRoute,
  path: "/general",
  component: ProjectGeneralPage,
})
/** Decision 0129: the project's people, their roles, and who can see it. */
export const projectMembersRoute = createRoute({
  getParentRoute: () => projectSettingsRoute,
  path: "/members",
  component: ProjectMembersPage,
})
export const projectReposRoute = createRoute({
  getParentRoute: () => projectSettingsRoute,
  path: "/repos",
  component: ProjectReposPage,
})
export const projectAreasRoute = createRoute({
  getParentRoute: () => projectSettingsRoute,
  path: "/areas",
  component: ProjectAreasPage,
})
export const projectAgentsRoute = createRoute({
  getParentRoute: () => projectSettingsRoute,
  path: "/agents",
  component: ProjectAgentsPage,
})
export const projectAgentNewRoute = createRoute({
  getParentRoute: () => projectSettingsRoute,
  path: "/agents/new",
  component: ProjectAgentNewPage,
})
/**
 * Wave C (decision 0071, spec §3.1): an agent's settings moved to her page's Settings tab. The page takes her id as
 * well as her handle, so the redirect needs no project state.
 */
export const projectAgentRoute = createRoute({
  getParentRoute: () => projectSettingsRoute,
  path: "/agents/$agentId",
  beforeLoad: ({ params, location }) => {
    throw redirect({
      to: "/w/$workspaceId/p/$projectId/agents/$handle",
      params: { workspaceId: params.workspaceId, projectId: params.projectId, handle: params.agentId },
      search: { tab: "settings" },
      ...(location.hash ? { hash: location.hash } : {}),
      replace: true,
    })
  },
  component: ProjectAgentPage,
})
export const projectWorkersRoute = createRoute({
  getParentRoute: () => projectSettingsRoute,
  path: "/workers",
  component: ProjectWorkersPage,
})
/** Decision 0096: the project's sandbox allowlist. */
export const projectAllowlistRoute = createRoute({
  getParentRoute: () => projectSettingsRoute,
  path: "/allowed",
  component: ProjectAllowlistPage,
})
/** Decision 0141: the secrets this project's runs can use. */
export const projectSecretsRoute = createRoute({
  getParentRoute: () => projectSettingsRoute,
  path: "/secrets",
  component: ProjectSecretsPage,
})

// Old runner links (bookmarks, CLI output) → their settings pages.
const runnersRedirectRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/runners",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/w/$workspaceId/settings/runners", params, replace: true })
  },
})
const runnerNewRedirectRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/runners/new",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/w/$workspaceId/settings/runners/new", params, replace: true })
  },
})
const runnerDetailRedirectRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/runners/$runnerId",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/w/$workspaceId/settings/runners/$runnerId", params, replace: true })
  },
})

export const settingsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/settings",
  component: SettingsLayout,
})
export const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    if (wideScreen()) throw redirect({ to: "/w/$workspaceId/settings/general", params, replace: true })
  },
  component: SettingsIndex,
})
// Wave B2 (decision 0057): the account pages left workspace settings; old links redirect.
export const settingsProfileRedirectRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/account/profile",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/w/$workspaceId/account/profile", params, replace: true })
  },
})
export const settingsSecurityRedirectRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/account/security",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/w/$workspaceId/account/security", params, replace: true })
  },
})
export const settingsGeneralRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/general",
  component: SettingsGeneralPage,
})
export const settingsMembersRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/members",
  // decision 0130: `project` preselects the project(s) the invitee joins (invited from that project)
  validateSearch: (search: Record<string, unknown>): { invite?: 1; project?: string } => ({
    ...(search.invite === 1 || search.invite === "1" ? { invite: 1 as const } : {}),
    ...(typeof search.project === "string" && /^[A-Za-z0-9_-]{1,128}$/.test(search.project)
      ? { project: search.project }
      : {}),
  }),
  component: SettingsMembersPage,
})
export const runnersRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/runners",
  component: RunnersPage,
})
export const runnerNewRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/runners/new",
  component: RunnerNewPage,
})
export const runnerDetailRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/runners/$runnerId",
  validateSearch: (search: Record<string, unknown>): { profile?: string } =>
    typeof search.profile === "string" && /^[a-z0-9][a-z0-9_-]{0,63}$/i.test(search.profile)
      ? { profile: search.profile }
      : {},
  component: RunnerDetailPage,
})
export const settingsModelsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/models",
  component: SettingsModelsPage,
})
/** Decision 0166 §3.5: every subscription in the workspace, grouped by person. */
export const settingsSubscriptionsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/subscriptions",
  component: SettingsSubscriptionsPage,
})
/** Decision 0096: the workspace's sandbox allowlist. */
export const settingsAllowlistRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/allowed",
  component: SettingsAllowlistPage,
})
/** Decision 0141: the workspace's secrets for agents' runs. */
export const settingsSecretsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/secrets",
  component: SettingsSecretsPage,
})
/** Wave F (decision 0062): the workspace's usage; `?project=<id or slug>` narrows it to one project. */
export const settingsUsageRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/usage",
  validateSearch: (search: Record<string, unknown>): { project?: string } =>
    typeof search.project === "string" && /^[A-Za-z0-9_-]{1,64}$/.test(search.project)
      ? { project: search.project }
      : {},
  component: SettingsUsagePage,
})

/**
 * GitHub App S1 (decision 0069): the viewer's GitHub link, installations and repos. Better Auth comes back with
 * `?linked=1` or `?error=<code>`; the setup page with `?installed=<installation id>`.
 */
export const settingsGithubRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/github",
  validateSearch: (
    search: Record<string, unknown>,
  ): { linked?: 1; installed?: number; error?: string; link?: number; requested?: 1 } => ({
    ...(search.linked === 1 || search.linked === "1" ? { linked: 1 as const } : {}),
    // the setup URL's "Choose a workspace" page (link=<installation id>) and an org approval request (requested=1)
    ...((typeof search.link === "number" || typeof search.link === "string") &&
    /^[1-9]\d{0,15}$/.test(String(search.link))
      ? { link: Number(search.link) }
      : {}),
    ...(search.requested === 1 || search.requested === "1" ? { requested: 1 as const } : {}),
    ...(typeof search.installed === "number" || typeof search.installed === "string"
      ? /^[1-9]\d{0,15}$/.test(String(search.installed))
        ? { installed: Number(search.installed) }
        : {}
      : {}),
    ...(typeof search.error === "string" && /^[a-z_]{1,64}$/i.test(search.error)
      ? { error: search.error }
      : {}),
  }),
  component: SettingsGithubPage,
})

/**
 * Account (wave B2, decision 0057 §7): one home for what is the person's own, opened from the avatar menu on every page.
 * The workspace stays in the URL (an open point for Jannik, ia.md M3); the pages show nothing workspace-specific.
 */
export const accountRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/account",
  component: AccountLayout,
})
const accountIndexRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: "/",
  // onboarding week A2: a phone gets the Account list (Notifications is one row of it); wide screens Sign-in & security
  beforeLoad: ({ params }) => {
    if (wideScreen()) throw redirect({ to: "/w/$workspaceId/account/security", params, replace: true })
  },
  component: AccountIndex,
})
/** UX pass phase 2 (P11): Profile folded into "Your page" in Knowledge; old links land there. */
export const accountProfileRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: "/profile",
  beforeLoad: ({ params, context }) => {
    throw redirect({
      to: "/w/$workspaceId/knowledge/people/$userId",
      params: { workspaceId: params.workspaceId, userId: context.session.user.id },
      replace: true,
    })
  },
})
export const accountSecurityRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: "/security",
  component: AccountSecurityPage,
})
/** Wave F (decision 0062): Your plans, the person's own plans and keys across workspaces. */
export const accountUsageRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: "/usage",
  component: AccountUsagePage,
})
/** Decision 0167 §3: My subscriptions, the person's own accounts across workspaces. */
export const accountSubscriptionsRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: "/subscriptions",
  component: AccountSubscriptionsPage,
})
/** Decision 0113: install Minga, push notifications on this device, devices, what pushes. */
export const accountNotificationsRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: "/notifications",
  component: AccountNotificationsPage,
})
/** Decision 0146: Private memory, the person's own notes for their agents. */
export const accountMemoryRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: "/memory",
  component: AccountMemoryPage,
})
/** Connected apps (Claude Code, Codex): `?project=<slug or id>` fills the import command. */
export const accountAppsRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: "/apps",
  validateSearch: (search: Record<string, unknown>): { project?: string } =>
    typeof search.project === "string" && /^[A-Za-z0-9_-]{1,64}$/.test(search.project)
      ? { project: search.project }
      : {},
  component: AccountAppsPage,
})

const routeTree = rootRoute.addChildren([
  guestRoute.addChildren([signInRoute, signUpRoute, forgotPasswordRoute]),
  inviteRoute,
  resetPasswordRoute,
  oauthContinueRoute,
  oauthConsentRoute,
  appRoute.addChildren([
    homeRoute,
    welcomeRoute,
    pairRoute,
    workspaceRoute.addChildren([
      workspaceHomeRoute,
      knowledgeRoute,
      knowledgePersonRoute,
      knowledgeGuidelineRoute,
      knowledgeCapabilitiesRoute,
      projectRoute.addChildren([
        projectIndexRoute,
        itemRoute,
        goalRoute,
        kpiRoute,
        documentRoute,
        agentPageRoute,
        memoryRoute,
        projectSearchRoute,
        projectSettingsRoute.addChildren([
          projectSettingsIndexRoute,
          projectGeneralRoute,
          projectMembersRoute,
          projectReposRoute,
          projectAreasRoute,
          projectAgentsRoute,
          projectAgentNewRoute,
          projectAgentRoute,
          projectWorkersRoute,
          projectAllowlistRoute,
          projectSecretsRoute,
        ]),
      ]),
      runnersRedirectRoute,
      runnerNewRedirectRoute,
      runnerDetailRedirectRoute,
      accountRoute.addChildren([
        accountIndexRoute,
        accountProfileRoute,
        accountSecurityRoute,
        accountUsageRoute,
        accountSubscriptionsRoute,
        accountNotificationsRoute,
        accountMemoryRoute,
        accountAppsRoute,
      ]),
      settingsRoute.addChildren([
        settingsIndexRoute,
        settingsProfileRedirectRoute,
        settingsSecurityRedirectRoute,
        settingsGeneralRoute,
        settingsMembersRoute,
        runnersRoute,
        runnerNewRoute,
        runnerDetailRoute,
        settingsModelsRoute,
        settingsSubscriptionsRoute,
        settingsAllowlistRoute,
        settingsSecretsRoute,
        settingsUsageRoute,
        settingsGithubRoute,
      ]),
    ]),
  ]),
])

export const createAppRouter = (queryClient: QueryClient, history?: RouterHistory) =>
  createRouter({
    routeTree,
    context: { queryClient },
    ...(history ? { history } : {}),
    defaultPreload: "intent",
    defaultNotFoundComponent: () => <NotFound />,
  })

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
}

export type { ProjectSearch } from "./lib/project-search"
export { forgotPasswordRoute, signInRoute, signUpRoute }
